import _get from 'lodash/get'
import parseInt from 'lodash/parseInt'
import { List, fromJS } from 'immutable'
import { get as getConfig } from '../../config/index'
import { SET_APP_BOOTSTRAP_PHASE } from '../app/actions'
import { BOOTSTRAP_PHASE_INIT } from '../app/index'
import { AUTH_LOGOUT, SET_AUTH_LOGIN_SUCCESS, changeAuthProfile } from '../auth/actions'
import { SET_USER_DATA } from '../user/actions'
import { resetOnboarding } from '../onboarding/actions'
import { INTERSTITIAL_SELECT_PROFILE } from '../interstitial/index'
import { SET_CHECKOUT_ORDER_DATA } from '../checkout/actions'
import { removeInterstitial, setInterstitial } from '../interstitial/actions'
import { setSessionStorage, clearSessionStorage } from '../local-preferences/index'
import { scrollToTop } from '../dom/index'

import {
  PROMPT_PROFILE_SELECTOR_KEY,
  removeProfileFromAccount,
  initializePrompt,
  postUserProfile,
  getProfiles,
  getAccountProfilesGraphQL,
} from '.'

import {
  getProfiles as getProfilesAction,
  USER_PROFILES_SET_PROMPT,
  USER_PROFILES_CREATED,
  USER_PROFILES_SELECT,
  USER_PROFILES_REMOVE,
  USER_PROFILES_CREATE,
  USER_PROFILES_SET,
  USER_PROFILES_GET,
  showPrompt,
} from './actions'
import { shouldFetchProfilesOnClient } from './watchers-helpers'

function dispatchFetchProfilesIfNeeded ({ state, dispatch }) {
  if (shouldFetchProfilesOnClient(state)) {
    dispatch(getProfilesAction())
  }
}

export function watchBootstrapFetchProfiles ({ after }) {
  return after(SET_APP_BOOTSTRAP_PHASE, ({ state, dispatch, action }) => {
    if (_get(action, 'payload.phase') !== BOOTSTRAP_PHASE_INIT) {
      return
    }
    dispatchFetchProfilesIfNeeded({ state, dispatch })
  })
}

export function watchLoginFetchProfiles ({ after }) {
  return after(SET_AUTH_LOGIN_SUCCESS, ({ state, dispatch, action }) => {
    if (!action.payload?.success) {
      return
    }
    dispatchFetchProfilesIfNeeded({ state, dispatch })
  })
}

export function watchLogout ({ before }) {
  return before(AUTH_LOGOUT, () => {
    clearSessionStorage(PROMPT_PROFILE_SELECTOR_KEY)
  })
}

export function watchUserData ({ before }) {
  return before(SET_USER_DATA, ({ state, action, dispatch }) => {
    const { auth, user } = state
    const jwt = auth.get('jwt')
    const uid = _get(action, 'payload.data.account_owner_uid')
    if (uid !== user.getIn(['data', 'account_owner_uid'])) {
      dispatch(getProfilesAction(jwt))
    }
  })
}

export function watchSetOrderData ({ before }) {
  return before(SET_CHECKOUT_ORDER_DATA, ({ dispatch }) => {
    dispatch(showPrompt(false))
  })
}

export function watchShowPrompt ({ before }) {
  return before(USER_PROFILES_SET_PROMPT, ({ dispatch, action }) => {
    const { payload: show } = action
    const result = show
      ? setInterstitial(INTERSTITIAL_SELECT_PROFILE)
      : removeInterstitial(INTERSTITIAL_SELECT_PROFILE)
    setSessionStorage(PROMPT_PROFILE_SELECTOR_KEY, show)
    dispatch(result)
  })
}

// core watcher to fetch the user profiles
export function watchGetProfiles ({ after }) {
  return after([USER_PROFILES_GET], async ({ state, dispatch }) => {
    const { auth } = state
    const useCommunityProfile = _get(getConfig(), 'features.communityProfile')

    if (useCommunityProfile) {
      const graphqlResult = await getAccountProfilesGraphQL(auth, { slim: true })
      dispatch({
        payload: {
          promptProfileSelector: false,
          data: graphqlResult.userProfiles.data,
        },
        type: USER_PROFILES_SET,
      })
      return
    }

    const { data } = await getProfiles(auth)

    dispatch({
      payload: { promptProfileSelector: false, data },
      type: USER_PROFILES_SET,
    })
  })
}

export function watchUserProfileSet ({ after }) {
  return after([USER_PROFILES_SET], async ({ state, dispatch }) => {
    const {
      app, user, resolver, featureTracking, userProfiles,
    } = state
    const userProfilesData = List.isList(userProfiles.get('data')) ? userProfiles.get('data', List()) : List()
    const promptProfileSelector = initializePrompt({
      profileCount: userProfilesData.size,
      featureTracking,
      resolver,
      user,
      app,
    })

    dispatch(showPrompt(promptProfileSelector))
  })
}

// watcher for when a profile is selected from the
// multiple profile selector screen
export function watchSelectProfile ({ after }) {
  return after(USER_PROFILES_SELECT, async ({ state, action, dispatch }) => {
    const { userProfiles, auth } = state
    const { payload: index } = action
    const indexInt = parseInt(index)
    const profile = userProfiles.getIn(['data', indexInt])

    if (profile) {
      const uid = parseInt(profile.get('uid'))
      const userAccountId = profile.get('user_account_id')
      const shouldRedirect = uid !== auth.get('uid')
      dispatch(resetOnboarding())
      dispatch(changeAuthProfile({
        userAccountId,
        auth,
        uid,
        shouldRedirect,
      }))
      dispatch(showPrompt(false))
      scrollToTop()
    }
  })
}

// for when a new profile is created
export function watchCreateUserProfile ({ takeFirst }) {
  return takeFirst(USER_PROFILES_CREATE, async ({ state, action }) => {
    const { userProfiles } = state
    const { payload } = action
    const { data, error, message } = await postUserProfile(payload)
    const profiles = userProfiles.get('data', List())
    return {
      type: USER_PROFILES_CREATED,
      payload: {
        data: data ? profiles.push(fromJS(data)) : profiles,
        message,
        error,
      },
    }
  })
}

// for when a profile is removed
export function watchRemoveProfile ({ takeFirst }) {
  return takeFirst(USER_PROFILES_REMOVE, async ({ state, action }) => {
    const { userProfiles } = state
    const { payload } = action
    const { data = {}, error, message } = await removeProfileFromAccount(payload)
    const profiles = userProfiles.get('data', List())
    const { uid } = data
    return {
      type: USER_PROFILES_SET,
      payload: {
        data: error ? null : profiles.filter((p) => p.get('uid') !== uid),
        message,
        error,
      },
    }
  })
}
